/**
 * Oferta e punës si dokument i printueshëm.
 *
 * Deri tani oferta ekzistonte vetëm si rreshta te baza: paga, data e fillimit,
 * afati i përgjigjes. Kandidati merr një letër, jo një rresht — dhe ajo letër
 * duhet të thotë saktësisht të njëjtat kushte që ruajti HR-ja, përndryshe
 * mospërputhja zbulohet ditën e parë të punës.
 *
 * Nuk gjenerohet PDF me bibliotekë: dritarja e printimit e mbulon edhe
 * «Shkarko PDF» në çdo shfletues, kurse një gjenerues i dytë do të duhej ta
 * mbante të njëjtin layout në dy vende. I njëjti arsyetim si te
 * `handoverPrint.js`, dhe qëllimisht i njëjti stil — dy dokumente të kompanisë
 * që duken si dy kompani janë një problem i vogël që lexohet menjëherë.
 */

import { employmentTypeLabel } from '../config/recruitmentPipeline';

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
));

const day = (v) => {
  const s = String(v || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '—';
  const [y, m, d] = s.split('-');
  return `${d}.${m}.${y}`;
};

const money = (v, cur) => (v === null || v === undefined || v === ''
  ? '—'
  : `${Number(v).toLocaleString('sq-AL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur || 'EUR'}`);

/**
 * Vetëm një ofertë e dërguar është ofertë.
 *
 * Draft-i dhe ajo në pritje të miratimit printohen me vulën DRAFT mbi faqe: pa
 * të, një draft i printuar nga tavolina do të dukej i njëjtë me atë zyrtarin,
 * dhe kandidati do ta lexonte si premtim.
 */
const isFinal = (status) => ['sent', 'accepted'].includes(String(status || ''));

export function offerHtml(o, { company = 'NEXUMA', signerName = '', signerRole = 'Burimet Njerëzore' } = {}) {
  const rows = [
    ['Pozita', o.position || '—'],
    ['Lloji i angazhimit', employmentTypeLabel(o.employmentType)],
    ['Paga bruto', money(o.salary, o.currency)],
    ['Shpërblime shtesë', o.bonus || '—'],
    ['Data e fillimit', day(o.startDate)],
    ['Periudha provuese', o.probationMonths ? `${o.probationMonths} muaj` : '—'],
    ['Afati i përgjigjes', day(o.expiresOn)],
  ].map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('');

  return `<!DOCTYPE html>
<html lang="sq"><head><meta charset="UTF-8" />
<title>Ofertë pune — ${esc(o.candidateName || '')}</title>
<style>
  @page { size: A4; margin: 18mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color:#0F172A; font-size:11.5px; margin:0; line-height:1.6; }
  .head { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #3E8FA3; padding-bottom:8px; }
  .brand { font-size:18px; font-weight:800; color:#3E8FA3; letter-spacing:.5px; }
  .meta { text-align:right; font-size:10px; color:#475569; }
  h1 { font-size:14px; text-align:center; margin:18px 0 6px; letter-spacing:.6px; }
  .sub { text-align:center; color:#64748B; font-size:10px; margin-bottom:18px; }
  .muted { color:#64748B; font-size:9.5px; }
  .label { font-size:8.5px; text-transform:uppercase; letter-spacing:.06em; color:#64748B; font-weight:700; }
  table { width:100%; border-collapse:collapse; margin:10px 0 14px; }
  th { background:#F1F5F9; text-align:left; padding:7px 8px; border:1px solid #E2E8F0; width:38%; font-size:10.5px; }
  td { padding:7px 8px; border:1px solid #E2E8F0; font-weight:600; }
  .decl { border:1px solid #E2E8F0; background:#F8FAFC; padding:11px; border-radius:4px; margin:14px 0 22px; }
  .sigs { display:flex; gap:40px; margin-top:34px; }
  .sig { flex:1; text-align:center; }
  .sig-blank { height:46px; }
  .sig-line { border-top:1px solid #0F172A; margin-top:2px; }
  .sig-name { font-weight:700; margin-top:3px; }
  .draft { position:fixed; top:42%; left:14%; font-size:78px; color:rgba(148,163,184,.22);
           transform:rotate(-24deg); font-weight:800; letter-spacing:8px; }
  @media print { .noprint { display:none } }
</style></head>
<body>
  ${isFinal(o.status) ? '' : '<div class="draft">DRAFT</div>'}
  <div class="head">
    <div><div class="brand">${esc(company)}</div><div class="muted">Burimet Njerëzore</div></div>
    <div class="meta">
      <div>Nr: <strong>OF-${esc(o.id)}</strong></div>
      <div>Data: ${day(o.sentAt || o.createdAt)}</div>
    </div>
  </div>

  <h1>OFERTË PUNE</h1>
  <div class="sub">Dokument i lëshuar nga ${esc(company)}</div>

  <p>I/E nderuar <strong>${esc(o.candidateName || '—')}</strong>,</p>
  <p>
    Pas procesit të vlerësimit, kemi kënaqësinë t'ju ofrojmë pozitën
    <strong>${esc(o.position || '—')}</strong> me kushtet e mëposhtme:
  </p>

  <table><tbody>${rows}</tbody></table>

  ${o.notes ? `<div><span class="label">Shënime</span><div>${esc(o.notes)}</div></div>` : ''}

  <div class="decl">
    Kjo ofertë është e vlefshme deri më <strong>${day(o.expiresOn)}</strong>. Pranimi konfirmohet
    me nënshkrimin e këtij dokumenti ose me përgjigje me shkrim brenda afatit. Kushtet e plota
    të punësimit rregullohen me kontratën e punës që nënshkruhet para ditës së parë të punës.
  </div>

  <div class="sigs">
    <div class="sig">
      <div class="sig-blank"></div><div class="sig-line"></div>
      <div class="sig-name">${esc(signerName || '—')}</div>
      <div class="muted">${esc(signerRole)}, ${esc(company)}</div>
    </div>
    <div class="sig">
      <div class="sig-blank"></div><div class="sig-line"></div>
      <div class="sig-name">${esc(o.candidateName || '—')}</div>
      <div class="muted">Kandidati — pranoj kushtet e mësipërme</div>
    </div>
  </div>
</body></html>`;
}

/** Hap dritaren e printimit. Kthen `false` kur bllokuesi i pop-up-eve e ndal. */
export function printOffer(o, opts) {
  const win = window.open('', '_blank', 'width=900,height=1000,scrollbars=yes');
  if (!win) return false;
  win.document.write(offerHtml(o, opts));
  win.document.close();
  win.focus();
  win.onload = () => setTimeout(() => win.print(), 200);
  return true;
}
